import { Link } from "@tanstack/react-router";

import { cn } from "../../lib/cn";

interface AnalyticsTab {
  to: string;
  label: string;
}

// The single Analytics surface fronts six sections. The sidebar dropdown that
// once listed these collapsed into one "Analytics" nav entry (ADR 0024 D4);
// the sections now live as in-page tabs so none of the report routes are
// orphaned from navigation.
const ANALYTICS_TABS: ReadonlyArray<AnalyticsTab> = [
  { to: "/analytics", label: "Overview" },
  { to: "/reports/equity-curve", label: "Equity Curve" },
  { to: "/reports/overall-statistics", label: "Statistics" },
  // P&L Charts + Win-Rate Charts merged into one "Charts" tab with an in-page
  // metric toggle (Analytics restructure Unit 2). The route keeps its
  // profit-loss-charts path; /reports/win-rate-charts redirects here.
  { to: "/reports/profit-loss-charts", label: "Charts" },
  { to: "/reports/tags", label: "Tag Performance" },
];

// Layout-only base; the active/inactive colour sets are supplied via
// active/inactiveProps so there is no Tailwind border/text source-order clash.
const TAB_BASE =
  "inline-flex shrink-0 items-center rounded-control border px-3 py-1.5 text-sm font-medium transition-colors";
const TAB_ACTIVE = "border-primary bg-surface-container-high text-on-surface";
const TAB_INACTIVE =
  "border-transparent text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface";

export function AnalyticsTabs() {
  return (
    <div
      role="tablist"
      aria-label="Analytics sections"
      className="flex items-center gap-1 overflow-x-auto border-b border-outline-variant px-6 py-3"
    >
      {ANALYTICS_TABS.map((tab) => (
        <Link
          key={tab.to}
          to={tab.to}
          role="tab"
          // Carry the active Analytics filter across section switches. Without
          // this the URL search drops on tab change (we no longer retain search
          // app-wide), so the chosen account/tag/date would reset per tab.
          search={(prev) => prev}
          className={TAB_BASE}
          // Overview (/analytics) is a prefix of nothing it shares, and the
          // report routes are distinct full paths, but exact matching keeps
          // exactly one tab selected regardless of future nesting.
          activeOptions={{ exact: true }}
          activeProps={{ className: cn(TAB_ACTIVE), "aria-selected": true }}
          inactiveProps={{ className: cn(TAB_INACTIVE), "aria-selected": false }}
        >
          {tab.label}
        </Link>
      ))}
    </div>
  );
}
